import { NavLink, Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Home, Plus, LayoutDashboard, Package, Truck, LogOut, ArrowLeftRight, User } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const TONE = "hsl(var(--status-leave))";

function roleLabel(role) {
  return {
    sales_team: "Sales",
    management: "Management",
    dispatch_team: "Dispatch",
    admin: "Admin",
  }[role] || role;
}

export default function QuotesLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const initials = (user?.name || user?.email || "?")
    .split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const role = user?.role;
  const isSales = role === "sales_team" || role === "admin";
  const isMgmt = role === "management" || role === "admin";
  const isDispatch = role === "dispatch_team" || role === "admin";

  // Bottom nav items: shown by role
  const items = [
    { to: "/quotes", label: "Quotes", icon: Package, testid: "quotes-nav-list", show: true, end: true },
    isSales && { to: "/quotes/new", label: "New", icon: Plus, testid: "quotes-nav-new", show: true, primary: true },
    isMgmt && { to: "/quotes/dashboard", label: "Board", icon: LayoutDashboard, testid: "quotes-nav-dashboard", show: true },
    isDispatch && { to: "/quotes?view=dispatch", label: "Dispatch", icon: Truck, testid: "quotes-nav-dispatch", show: true },
  ].filter(Boolean);

  return (
    <div className="min-h-screen grain-bg relative flex flex-col">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-card/85 backdrop-blur-md border-b border-border">
        <div className="mx-auto max-w-4xl px-4 py-3 flex items-center justify-between gap-3">
          <Link to="/quotes" className="flex items-center gap-2.5" data-testid="quotes-brand-link">
            <div className="h-9 w-9 rounded-xl grid place-items-center shadow-soft" style={{ background: TONE, color: "white" }}>
              <Package className="h-4 w-4" strokeWidth={2} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground font-mono-mini font-medium leading-none whitespace-nowrap mb-1">
                SpaceAttire Teams
              </div>
              <div className="font-display text-base font-bold leading-none tracking-tight">Quotes · {roleLabel(role)}</div>
            </div>
          </Link>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="quotes-user-menu-trigger"
                className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
              >
                <Avatar className="h-8 w-8">
                  {user?.picture ? <AvatarImage src={user.picture} alt={user?.name} /> : null}
                  <AvatarFallback className="text-[11px]">{initials}</AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground">Signed in as</DropdownMenuLabel>
              <DropdownMenuLabel className="pt-0 pb-2 truncate">
                <div className="text-sm font-medium truncate">{user?.name}</div>
                <div className="text-[11px] text-muted-foreground truncate">{user?.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild data-testid="quotes-back-to-hub-btn">
                <Link to="/"><ArrowLeftRight className="h-4 w-4 mr-2" /> Module chooser</Link>
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="quotes-logout-btn"
                onClick={async () => { await logout(); navigate("/quotes/login"); }}
                className="text-destructive focus:text-destructive"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Main */}
      <main className="flex-1 mx-auto w-full max-w-4xl pb-24" data-testid="quotes-main">
        <Outlet />
      </main>

      {/* Bottom nav */}
      <nav className="fixed bottom-0 inset-x-0 z-40 border-t border-border bg-card/85 backdrop-blur-md">
        <div className="mx-auto max-w-4xl px-2 py-1.5 grid" style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}>
          {items.map(({ to, label, icon: Icon, testid, primary }) => {
            const isActive =
              to === "/quotes"
                ? location.pathname === "/quotes"
                : location.pathname.startsWith(to.split("?")[0]);
            return (
              <NavLink
                key={to}
                to={to}
                end={to === "/quotes"}
                data-testid={testid}
                className={[
                  "relative flex flex-col items-center justify-center gap-1 py-2 rounded-lg transition-colors",
                  isActive ? "text-foreground" : "text-muted-foreground",
                ].join(" ")}
              >
                <span
                  className={[
                    "h-10 w-10 rounded-xl grid place-items-center transition-transform btn-lift",
                    primary ? "text-white shadow-soft" : (isActive ? "bg-brand text-white shadow-soft" : ""),
                  ].join(" ")}
                  style={primary ? { background: TONE } : undefined}
                >
                  <Icon className="h-4 w-4" strokeWidth={2} />
                </span>
                <span className="text-[10px] uppercase tracking-wider font-mono-mini font-medium">{label}</span>
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
